import { useState, useEffect, useRef, useCallback } from "react";
import * as recharts from "recharts";
const { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar } = recharts;

// ══════════════════════════════════════════════
// SEHAT SAATHI — Personal Health Operating System
// ══════════════════════════════════════════════

// ── Mock Data ──
const MOCK_VITALS_WEEK = Array.from({ length: 7 }, (_, i) => {
  const d = new Date(); d.setDate(d.getDate() - 6 + i);
  return {
    day: d.toLocaleDateString("en", { weekday: "short" }),
    hr: 68 + Math.floor(Math.random() * 12),
    spo2: 96 + Math.floor(Math.random() * 3),
    hrv: 35 + Math.floor(Math.random() * 20),
    steps: 4000 + Math.floor(Math.random() * 8000),
    sleep: +(5.5 + Math.random() * 3).toFixed(1),
    temp: +(36.2 + Math.random() * 0.8).toFixed(1),
    calories: 1800 + Math.floor(Math.random() * 700),
    respRate: 14 + Math.floor(Math.random() * 4),
  };
});

const MOCK_VITALS_MONTH = Array.from({ length: 30 }, (_, i) => {
  const d = new Date(); d.setDate(d.getDate() - 29 + i);
  return {
    day: d.toLocaleDateString("en", { month: "short", day: "numeric" }),
    hr: 66 + Math.floor(Math.random() * 14),
    spo2: 95 + Math.floor(Math.random() * 4),
    hrv: 30 + Math.floor(Math.random() * 25),
    steps: 3000 + Math.floor(Math.random() * 10000),
    sleep: +(4.5 + Math.random() * 4).toFixed(1),
    temp: +(36.0 + Math.random() * 1.0).toFixed(1),
    calories: 1600 + Math.floor(Math.random() * 900),
    respRate: 13 + Math.floor(Math.random() * 5),
  };
});

const MOCK_VITALS_3MONTH = Array.from({ length: 12 }, (_, i) => {
  const d = new Date(); d.setDate(d.getDate() - 84 + i * 7);
  return {
    day: d.toLocaleDateString("en", { month: "short", day: "numeric" }),
    hr: 65 + Math.floor(Math.random() * 15),
    spo2: 95 + Math.floor(Math.random() * 4),
    hrv: 28 + Math.floor(Math.random() * 28),
    steps: 3500 + Math.floor(Math.random() * 9000),
    sleep: +(5 + Math.random() * 3.5).toFixed(1),
    temp: +(36.1 + Math.random() * 0.9).toFixed(1),
    calories: 1700 + Math.floor(Math.random() * 800),
    respRate: 13 + Math.floor(Math.random() * 5),
  };
});

const VITALS_CONFIG = [
  { key: "hr", label: "Heart Rate", unit: "bpm", color: "#f87171", icon: "💓", range: "60-100", ideal: "60-80" },
  { key: "spo2", label: "SpO2", unit: "%", color: "#60a5fa", icon: "🫁", range: "95-100", ideal: "97-99" },
  { key: "hrv", label: "HRV", unit: "ms", color: "#a78bfa", icon: "📊", range: "20-70", ideal: "40-60" },
  { key: "sleep", label: "Sleep", unit: "hrs", color: "#818cf8", icon: "🌙", range: "4-10", ideal: "7-9" },
  { key: "steps", label: "Steps", unit: "", color: "#34d399", icon: "🏃", range: "5000-15000", ideal: "8000-12000" },
  { key: "temp", label: "Temperature", unit: "°C", color: "#fb923c", icon: "🌡️", range: "36-37.5", ideal: "36.1-37.2" },
  { key: "calories", label: "Calories", unit: "kcal", color: "#60a5fa", icon: "🔥", range: "1500-2500", ideal: "2000-2200" },
  { key: "respRate", label: "Respiratory Rate", unit: "br/min", color: "#2dd4bf", icon: "🌬️", range: "12-20", ideal: "12-18" },
];

const MOCK_NOTIFICATIONS = [
  { id: 1, type: "warning", title: "Elevated Resting Heart Rate", body: "Your resting HR averaged 82 bpm over the last 3 days — 15% above your baseline.", time: "2h ago", icon: "💓" },
  { id: 2, type: "alert", title: "Sleep Deficit Detected", body: "You've averaged 5.2 hrs/night this week. Students need 7-8 hrs for optimal cognitive function.", time: "6h ago", icon: "🌙" },
  { id: 3, type: "info", title: "Step Goal Streak", body: "You've hit 8,000+ steps for 5 consecutive days!", time: "1d ago", icon: "🏃" },
  { id: 4, type: "warning", title: "SpO2 Dip During Sleep", body: "Your blood oxygen dropped to 93% briefly last night.", time: "1d ago", icon: "🫁" },
];

const FOOD_PREFS = ["Vegetarian", "Non-Vegetarian", "Vegan", "Eggetarian", "Pescatarian", "Jain"];
const SPORTS = ["Badminton", "Cricket", "Football", "Swimming", "Tennis", "Running", "Gym/Weights", "Yoga", "Table Tennis", "Squash", "Basketball", "Cycling"];
const CONDITIONS = ["Diabetes", "Hypertension", "Asthma", "Thyroid", "PCOS/PCOD", "Anemia", "Heart condition", "None"];

const MOOD_OPTIONS = [
  { emoji: "😄", label: "Great", value: 5, color: "#4ade80" },
  { emoji: "🙂", label: "Good", value: 4, color: "#86efac" },
  { emoji: "😐", label: "Okay", value: 3, color: "#fbbf24" },
  { emoji: "😔", label: "Low", value: 2, color: "#fb923c" },
  { emoji: "😢", label: "Bad", value: 1, color: "#f87171" },
];

const FEELING_TAGS = ["Energetic", "Calm", "Anxious", "Tired", "Stressed", "Happy", "Focused", "Lonely", "Grateful", "Overwhelmed", "Motivated", "Irritable", "Peaceful", "Restless", "Hopeful"];

const WORKOUT_TEMPLATES = {
  "Upper Body": [
    { name: "Push-ups", sets: 3, reps: "12-15", muscle: "Chest", rest: 60 },
    { name: "Dumbbell Rows", sets: 3, reps: "10-12", muscle: "Back", rest: 60 },
    { name: "Shoulder Press", sets: 3, reps: "10-12", muscle: "Shoulders", rest: 60 },
    { name: "Bicep Curls", sets: 3, reps: "12-15", muscle: "Arms", rest: 45 },
    { name: "Tricep Dips", sets: 3, reps: "10-12", muscle: "Arms", rest: 45 },
    { name: "Plank", sets: 3, reps: "30-45s", muscle: "Core", rest: 30 },
  ],
  "Lower Body": [
    { name: "Squats", sets: 4, reps: "12-15", muscle: "Quads", rest: 60 },
    { name: "Lunges", sets: 3, reps: "10 each", muscle: "Quads/Glutes", rest: 60 },
    { name: "Deadlifts", sets: 3, reps: "10-12", muscle: "Hamstrings", rest: 90 },
    { name: "Calf Raises", sets: 3, reps: "15-20", muscle: "Calves", rest: 30 },
    { name: "Leg Press", sets: 3, reps: "12-15", muscle: "Quads", rest: 60 },
    { name: "Glute Bridges", sets: 3, reps: "12-15", muscle: "Glutes", rest: 45 },
  ],
  "Cardio & Core": [
    { name: "Jumping Jacks", sets: 3, reps: "30s", muscle: "Full Body", rest: 20 },
    { name: "High Knees", sets: 3, reps: "30s", muscle: "Cardio", rest: 20 },
    { name: "Burpees", sets: 3, reps: "8-10", muscle: "Full Body", rest: 45 },
    { name: "Mountain Climbers", sets: 3, reps: "30s", muscle: "Core/Cardio", rest: 20 },
    { name: "Bicycle Crunches", sets: 3, reps: "20", muscle: "Core", rest: 30 },
    { name: "Russian Twists", sets: 3, reps: "20", muscle: "Core", rest: 30 },
  ],
  "Yoga Flow": [
    { name: "Sun Salutation A", sets: 3, reps: "5 breaths each", muscle: "Full Body", rest: 10 },
    { name: "Warrior I & II", sets: 2, reps: "30s each side", muscle: "Legs/Balance", rest: 10 },
    { name: "Triangle Pose", sets: 2, reps: "30s each side", muscle: "Core/Legs", rest: 10 },
    { name: "Downward Dog", sets: 2, reps: "45s", muscle: "Full Body", rest: 10 },
    { name: "Pigeon Pose", sets: 2, reps: "30s each side", muscle: "Hips", rest: 10 },
    { name: "Savasana", sets: 1, reps: "3 min", muscle: "Recovery", rest: 0 },
  ],
  "Quick 15-min": [
    { name: "Jumping Jacks", sets: 2, reps: "30s", muscle: "Warmup", rest: 15 },
    { name: "Push-ups", sets: 2, reps: "10-12", muscle: "Chest", rest: 30 },
    { name: "Squats", sets: 2, reps: "15", muscle: "Legs", rest: 30 },
    { name: "Plank", sets: 2, reps: "30s", muscle: "Core", rest: 20 },
    { name: "Burpees", sets: 2, reps: "8", muscle: "Full Body", rest: 30 },
  ],
};

// ── System Prompts ──
const SYSTEM_PROMPT_REPORT = `You are Sehat Saathi, a medical report translator. You receive extracted text from a medical report and optional user context.
Respond ONLY in valid JSON:
{"parameters":[{"name":"...","value":"...","range":"...","status":"normal|borderline|critical","explanation":"...","contextNote":"..."}],"lifestyle":["..."],"doctorFlags":[{"issue":"...","urgency":"routine|soon|urgent"}],"summary":"..."}
Rules: Never diagnose. Say "may suggest" not "you have". When in doubt, recommend a doctor. Warm, zero jargon.`;

const SYSTEM_PROMPT_PLAN = `You are Sehat Saathi, a health & wellness AI. Based on the user's profile, create a personalized weekly plan.
Respond ONLY in valid JSON:
{"dietPlan":{"philosophy":"...","meals":[{"meal":"...","time":"...","suggestion":"...","calories":0,"notes":"..."}]},"trainingPlan":{"philosophy":"...","weeklySchedule":[{"day":"Mon","activity":"...","duration":"...","intensity":"low|moderate|high","notes":"..."}]},"sleepAdvice":"...","keyInsights":["...","...","..."]}
Tailor EVERYTHING to their situation. Be realistic, practical, culturally relevant (Indian food).`;

const SYSTEM_PROMPT_WORKOUT = `You are Sehat Saathi's workout AI. The user wants to start a workout. Based on their chosen workout type, special considerations (injuries, pain, limitations), and profile, modify the exercise list.
Respond ONLY in valid JSON:
{"exercises":[{"name":"...","sets":3,"reps":"...","muscle":"...","rest":60,"modified":false,"modNote":"..."}],"warmupAdvice":"...","considerations":"...","cooldownAdvice":"..."}
If the user mentions pain/injury (e.g. "back hurts"), REMOVE or REPLACE exercises that stress that area and explain why. Add suitable alternatives. Be specific and safe.`;

// ── Status Config ──
const statusCfg = {
  normal: { emoji: "✅", color: "#4ade80", bg: "rgba(74,222,128,0.1)", border: "rgba(74,222,128,0.25)" },
  borderline: { emoji: "⚠️", color: "#fbbf24", bg: "rgba(251,191,36,0.1)", border: "rgba(251,191,36,0.25)" },
  critical: { emoji: "🔴", color: "#f87171", bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.25)" },
};

// ── Pre-loaded Demo Reports & Saved Analysis ──
const DEMO_REPORT_1 = {
  id: 1001, name: "Blood Test - IIT Delhi Hospital (Apr 2026)", date: "11-Apr-2026",
  size: "48 KB",
  text: `IIT DELHI HOSPITAL & DIAGNOSTIC CENTRE
NABL Accredited Laboratory | ISO 15189:2022 Certified
IIT Delhi Campus, Hauz Khas, New Delhi - 110016
Patient Name: Amrutanshu Dash  Report Date: 11-Apr-2026
Age/Sex: 20 Years / Male  Sample Collected: 11-Apr-2026, 7:15 AM
Patient ID: IITD-HC-2026-1749  Referred By: Dr. S.K. Gupta
Sample Type: Venous Blood (Fasting)  Entry No: 2025CH71749
Address: Karakoram Hostel, IIT Delhi

COMPLETE BLOOD COUNT (CBC)
Hemoglobin 14.8 g/dL (13.0 - 17.0) Normal
RBC Count 5.2 million/uL (4.5 - 5.5) Normal
WBC Count 10800 /uL (4000 - 11000) Normal
Platelet Count 2.35 lakh/uL (1.5 - 4.0) Normal
PCV / Hematocrit 44.1 % (40.0 - 50.0) Normal
MCV 84.8 fL (80.0 - 100.0) Normal
MCH 28.5 pg (27.0 - 32.0) Normal
MCHC 33.6 g/dL (31.5 - 34.5) Normal
RDW-CV 13.4 % (11.5 - 14.5) Normal
Neutrophils 65 % (40 - 70) Normal
Lymphocytes 26 % (20 - 40) Normal
ESR 8 mm/hr (0 - 15) Normal

BLOOD SUGAR
Fasting Blood Sugar 92 mg/dL (70 - 100) Normal
HbA1c 5.3 % (< 5.7) Normal

LIPID PROFILE
Total Cholesterol 195 mg/dL (< 200) Normal
HDL Cholesterol 44 mg/dL (> 40) Normal
LDL Cholesterol 118 mg/dL (< 130) Normal
Triglycerides 162 mg/dL (< 150) HIGH
VLDL Cholesterol 32.4 mg/dL (< 30) HIGH

VITAMINS & MINERALS
Vitamin D (25-OH) 16.2 ng/mL (30 - 100) LOW
Vitamin B12 195 pg/mL (200 - 900) LOW
Iron, Serum 72 ug/dL (60 - 170) Normal
Ferritin 45 ng/mL (30 - 400) Normal
Calcium 9.1 mg/dL (8.5 - 10.5) Normal

THYROID FUNCTION TEST
TSH 3.2 uIU/mL (0.4 - 4.0) Normal
Free T4 1.3 ng/dL (0.8 - 1.8) Normal

LIVER FUNCTION TEST
SGOT (AST) 38 U/L (0 - 40) Normal
SGPT (ALT) 44 U/L (0 - 41) HIGH
Bilirubin Total 0.8 mg/dL (0.1 - 1.2) Normal
Alkaline Phosphatase 92 U/L (44 - 147) Normal
GGT 35 U/L (0 - 55) Normal
Total Protein 7.4 g/dL (6.0 - 8.3) Normal
Albumin 4.6 g/dL (3.5 - 5.5) Normal

KIDNEY FUNCTION TEST
Urea 28 mg/dL (17 - 43) Normal
Creatinine 1.0 mg/dL (0.7 - 1.3) Normal
Uric Acid 7.5 mg/dL (3.5 - 7.2) HIGH
eGFR 105 mL/min (> 90) Normal
Sodium 141 mEq/L (136 - 146) Normal
Potassium 4.3 mEq/L (3.5 - 5.1) Normal`,
};

const DEMO_REPORT_2 = {
  id: 1002, name: "Blood Test - SUM Hospital Bhubaneswar (Jan 2026)", date: "15-Jan-2026",
  size: "42 KB",
  text: `SUM HOSPITAL DIAGNOSTICS
NABL Accredited | ISO 15189:2022 Certified
Kalinga Nagar, Bhubaneswar, Odisha - 751003
Patient Name: Amrutanshu Dash  Report Date: 15-Jan-2026
Age/Sex: 20 Years / Male  Sample Collected: 15-Jan-2026, 7:45 AM
Patient ID: SUM-2026-01-4412  Referred By: Dr. R.N. Panda
Sample Type: Venous Blood (Fasting)
Address: Bhubaneswar, Odisha

COMPLETE BLOOD COUNT (CBC)
Hemoglobin 13.6 g/dL (13.0 - 17.0) Normal
RBC Count 4.8 million/uL (4.5 - 5.5) Normal
WBC Count 8400 /uL (4000 - 11000) Normal
Platelet Count 2.50 lakh/uL (1.5 - 4.0) Normal
PCV / Hematocrit 41.2 % (40.0 - 50.0) Normal
MCV 85.8 fL (80.0 - 100.0) Normal
MCH 28.3 pg (27.0 - 32.0) Normal
MCHC 33.0 g/dL (31.5 - 34.5) Normal
ESR 10 mm/hr (0 - 15) Normal

BLOOD SUGAR
Fasting Blood Sugar 88 mg/dL (70 - 100) Normal
HbA1c 5.1 % (< 5.7) Normal

LIPID PROFILE
Total Cholesterol 188 mg/dL (< 200) Normal
HDL Cholesterol 46 mg/dL (> 40) Normal
LDL Cholesterol 112 mg/dL (< 130) Normal
Triglycerides 148 mg/dL (< 150) Normal
VLDL Cholesterol 29.6 mg/dL (< 30) Normal

VITAMINS & MINERALS
Vitamin D (25-OH) 12.8 ng/mL (30 - 100) LOW
Vitamin B12 210 pg/mL (200 - 900) Normal
Iron, Serum 68 ug/dL (60 - 170) Normal
Ferritin 38 ng/mL (30 - 400) Normal
Calcium 8.8 mg/dL (8.5 - 10.5) Normal

THYROID FUNCTION TEST
TSH 2.8 uIU/mL (0.4 - 4.0) Normal
Free T4 1.4 ng/dL (0.8 - 1.8) Normal

LIVER FUNCTION TEST
SGOT (AST) 30 U/L (0 - 40) Normal
SGPT (ALT) 34 U/L (0 - 41) Normal
Bilirubin Total 0.7 mg/dL (0.1 - 1.2) Normal
Alkaline Phosphatase 85 U/L (44 - 147) Normal
Total Protein 7.1 g/dL (6.0 - 8.3) Normal
Albumin 4.4 g/dL (3.5 - 5.5) Normal

KIDNEY FUNCTION TEST
Urea 30 mg/dL (17 - 43) Normal
Creatinine 0.9 mg/dL (0.7 - 1.3) Normal
Uric Acid 6.8 mg/dL (3.5 - 7.2) Normal
eGFR 112 mL/min (> 90) Normal
Sodium 139 mEq/L (136 - 146) Normal
Potassium 4.1 mEq/L (3.5 - 5.1) Normal`,
};

const SAVED_ANALYSIS_1 = {
  summary: "Overall, you're in good shape for a 20-year-old active male. Your CBC, blood sugar, thyroid, and kidney function are all healthy. However, there are a few areas that need attention: your Vitamin D and B12 are both low, triglycerides are slightly elevated likely from hostel food, your liver enzyme SGPT is marginally above normal possibly from late nights and irregular sleep, and uric acid is high which can happen with intense physical activity and protein-heavy meals.",
  parameters: [
    { name: "Hemoglobin", value: "14.8 g/dL", range: "13.0 - 17.0", status: "normal", explanation: "Your hemoglobin is healthy, meaning your blood is carrying oxygen efficiently. Important for someone as physically active as you.", contextNote: null },
    { name: "RBC Count", value: "5.2 million/µL", range: "4.5 - 5.5", status: "normal", explanation: "Red blood cell count is right in the healthy range. Your body is producing enough cells to support your active lifestyle.", contextNote: null },
    { name: "WBC Count", value: "10,800 /µL", range: "4000 - 11000", status: "normal", explanation: "White blood cells are at the upper end of normal. This can happen after intense exercise or minor stress — nothing concerning on its own.", contextNote: "Being at the upper end is common for athletes who train regularly. If you had a tough workout or match the day before the test, that alone could explain it." },
    { name: "Platelet Count", value: "2.35 lakh/µL", range: "1.5 - 4.0", status: "normal", explanation: "Platelets are healthy. Your blood clotting ability is perfectly fine.", contextNote: null },
    { name: "Fasting Blood Sugar", value: "92 mg/dL", range: "70 - 100", status: "normal", explanation: "Your fasting sugar is well within the healthy range. No signs of pre-diabetes.", contextNote: null },
    { name: "HbA1c", value: "5.3%", range: "< 5.7", status: "normal", explanation: "This reflects your average blood sugar over the past 3 months. At 5.3%, you're solidly in the healthy zone.", contextNote: null },
    { name: "Triglycerides", value: "162 mg/dL", range: "< 150", status: "borderline", explanation: "Triglycerides are slightly above the recommended limit. These are fats in your blood that increase with oily, fried, or carb-heavy food.", contextNote: "Hostel mess food tends to be high in refined carbs and oil. This is likely the main driver. Cutting down on fried items at dinner and adding more dal/sabzi can bring this down quickly." },
    { name: "VLDL Cholesterol", value: "32.4 mg/dL", range: "< 30", status: "borderline", explanation: "VLDL carries triglycerides through your blood. It's slightly high because your triglycerides are elevated — they move together.", contextNote: "This will come down automatically once triglycerides improve. No separate action needed." },
    { name: "Total Cholesterol", value: "195 mg/dL", range: "< 200", status: "normal", explanation: "Just under the limit. Keeping it here is ideal — you don't want it creeping above 200.", contextNote: null },
    { name: "HDL Cholesterol", value: "44 mg/dL", range: "> 40", status: "normal", explanation: "HDL is your 'good' cholesterol that protects your heart. At 44, it's adequate but could be higher. Exercise and healthy fats boost HDL.", contextNote: "Your active sports schedule is likely helping keep this above 40. Keep it up — swimming and badminton are great for HDL." },
    { name: "LDL Cholesterol", value: "118 mg/dL", range: "< 130", status: "normal", explanation: "LDL (the 'bad' cholesterol) is in a healthy range. No concerns here.", contextNote: null },
    { name: "Vitamin D", value: "16.2 ng/mL", range: "30 - 100", status: "critical", explanation: "Your Vitamin D is significantly low — about half of what it should be. This affects bone strength, immunity, energy levels, and even mood.", contextNote: "Living in Delhi with indoor classes, hostel life, and pollution reducing UV exposure makes this extremely common. You almost certainly need supplementation — a weekly 60,000 IU dose is standard. Please consult a doctor for the right prescription." },
    { name: "Vitamin B12", value: "195 pg/mL", range: "200 - 900", status: "critical", explanation: "B12 is just below the normal cutoff. This vitamin is crucial for nerve function, energy, and making red blood cells. Low B12 can cause fatigue, brain fog, and tingling in hands/feet.", contextNote: "B12 deficiency is very common in Indian youth, especially with a vegetarian-heavy hostel diet. Consider B12-fortified foods or a daily supplement." },
    { name: "Iron", value: "72 µg/dL", range: "60 - 170", status: "normal", explanation: "Iron levels are adequate. Combined with your healthy hemoglobin, you're not iron-deficient.", contextNote: null },
    { name: "Ferritin", value: "45 ng/mL", range: "30 - 400", status: "normal", explanation: "Ferritin is your body's iron storage. It's on the lower side of normal but sufficient.", contextNote: null },
    { name: "TSH", value: "3.2 µIU/mL", range: "0.4 - 4.0", status: "normal", explanation: "Thyroid function is normal. Your metabolism is being regulated properly.", contextNote: null },
    { name: "Free T4", value: "1.3 ng/dL", range: "0.8 - 1.8", status: "normal", explanation: "Active thyroid hormone is in a healthy range.", contextNote: null },
    { name: "SGOT (AST)", value: "38 U/L", range: "0 - 40", status: "normal", explanation: "Liver enzyme AST is normal but on the higher side. Can be temporarily elevated after intense exercise.", contextNote: null },
    { name: "SGPT (ALT)", value: "44 U/L", range: "0 - 41", status: "borderline", explanation: "This liver enzyme is slightly above normal. ALT is more specific to the liver than AST. A mild elevation like this isn't alarming but worth monitoring.", contextNote: "Common causes at your age: irregular sleep, energy drinks, excessive caffeine, or heavy meals late at night. Try reducing these and retest in 3 months." },
    { name: "Uric Acid", value: "7.5 mg/dL", range: "3.5 - 7.2", status: "critical", explanation: "Uric acid is above the normal range. High levels over time can lead to joint pain (gout) or kidney stones. It's produced when your body breaks down purines found in red meat, organ meats, and certain protein-rich foods.", contextNote: "With your intense sports schedule (squash, badminton, cricket, swimming), muscle breakdown during exercise naturally raises uric acid. Combine that with protein-heavy hostel food and possibly not drinking enough water — it adds up. Stay very well hydrated (3-4 litres/day) and moderate protein intake." },
    { name: "Creatinine", value: "1.0 mg/dL", range: "0.7 - 1.3", status: "normal", explanation: "Kidney filtration is working well.", contextNote: null },
    { name: "Calcium", value: "9.1 mg/dL", range: "8.5 - 10.5", status: "normal", explanation: "Calcium is normal, but given your low Vitamin D, your body may not be absorbing it optimally.", contextNote: "Vitamin D helps your body absorb calcium. Fixing your Vitamin D will also improve calcium utilization for bone health." },
  ],
  doctorFlags: [
    { issue: "Vitamin D is significantly low at 16.2 ng/mL. You likely need a prescription supplement (60,000 IU weekly for 8 weeks is standard). A doctor can confirm the right dose and duration.", urgency: "soon" },
    { issue: "Vitamin B12 is below normal at 195 pg/mL. Consider daily supplementation or dietary changes. If you experience fatigue or tingling, consult a doctor.", urgency: "routine" },
    { issue: "Uric acid is elevated at 7.5 mg/dL. If you notice any joint pain or swelling, see a doctor promptly. Stay hydrated and moderate high-protein foods.", urgency: "routine" },
  ],
  lifestyle: [
    "Hydration is key — aim for 3-4 litres of water daily, especially with your sports schedule. This alone can help bring uric acid down.",
    "In the hostel mess, swap fried items for dal, curd, and sabzi at dinner. This can improve triglycerides within weeks.",
    "Get 15-20 minutes of direct sunlight between 10 AM - 2 PM when possible. Even sitting in the Karakoram courtyard helps with Vitamin D.",
    "Consider adding a B12 supplement (500-1000 mcg daily) — easily available at any pharmacy near campus.",
    "Try to get 7+ hours of sleep at least 5 nights a week. Your SGPT elevation may partly be from sleep disruption and late-night stress."
  ],
};

const SAVED_ANALYSIS_2 = {
  summary: "This report from January 2026, done at home in Bhubaneswar during winter break, is almost entirely clean. Your CBC, blood sugar, lipids, liver, kidney, and thyroid are all in healthy ranges. The only flag is Vitamin D at 12.8 ng/mL — even lower than your April report, which makes sense given it was peak winter with less sun exposure. Comparing the two reports, your overall health has remained stable, with some minor shifts from lifestyle changes after returning to campus.",
  parameters: [
    { name: "Hemoglobin", value: "13.6 g/dL", range: "13.0 - 17.0", status: "normal", explanation: "Healthy hemoglobin. Slightly lower than your April report (14.8), likely because you were less active during winter break.", contextNote: null },
    { name: "RBC Count", value: "4.8 million/µL", range: "4.5 - 5.5", status: "normal", explanation: "Red blood cell count is normal.", contextNote: null },
    { name: "WBC Count", value: "8,400 /µL", range: "4000 - 11000", status: "normal", explanation: "White blood cells are in a comfortable mid-range — lower than your April report (10,800), which makes sense since you weren't training as hard.", contextNote: null },
    { name: "Fasting Blood Sugar", value: "88 mg/dL", range: "70 - 100", status: "normal", explanation: "Blood sugar is excellent. Home food is clearly better for your glucose levels than hostel food.", contextNote: null },
    { name: "HbA1c", value: "5.1%", range: "< 5.7", status: "normal", explanation: "Three-month average blood sugar is perfect.", contextNote: null },
    { name: "Total Cholesterol", value: "188 mg/dL", range: "< 200", status: "normal", explanation: "Total cholesterol is well-controlled. Better than your April report (195) — home cooking makes a difference.", contextNote: null },
    { name: "HDL Cholesterol", value: "46 mg/dL", range: "> 40", status: "normal", explanation: "Good cholesterol is slightly higher than April (44). Balanced home meals may have helped.", contextNote: null },
    { name: "LDL Cholesterol", value: "112 mg/dL", range: "< 130", status: "normal", explanation: "Bad cholesterol is well within limits.", contextNote: null },
    { name: "Triglycerides", value: "148 mg/dL", range: "< 150", status: "normal", explanation: "Just under the limit at 148 — much better than your April reading of 162. Home food with less oil and refined carbs is the clear difference.", contextNote: "This proves that dietary changes alone can fix your triglycerides. When you return to hostel, try to replicate some of these eating habits." },
    { name: "Vitamin D", value: "12.8 ng/mL", range: "30 - 100", status: "critical", explanation: "Vitamin D is even lower here than in April (16.2). January in Bhubaneswar has shorter days and you likely spent more time indoors during break.", contextNote: "This has been persistently low across both reports. You need supplementation — this won't fix itself with lifestyle alone." },
    { name: "Vitamin B12", value: "210 pg/mL", range: "200 - 900", status: "normal", explanation: "B12 is just barely in the normal range at 210. Better than April (195), probably because home food includes more dairy and varied nutrition.", contextNote: "While technically normal, 210 is still quite low. The improvement from April suggests diet matters — but supplementation would give you a comfortable buffer." },
    { name: "SGOT (AST)", value: "30 U/L", range: "0 - 40", status: "normal", explanation: "Liver enzyme is well within normal range. Better than April (38).", contextNote: null },
    { name: "SGPT (ALT)", value: "34 U/L", range: "0 - 41", status: "normal", explanation: "Liver enzyme is normal here, compared to 44 (borderline) in April. Better sleep schedule and no energy drinks during break likely helped.", contextNote: "This confirms that your elevated SGPT in April is lifestyle-driven, not a liver issue. Manageable with sleep and diet." },
    { name: "Uric Acid", value: "6.8 mg/dL", range: "3.5 - 7.2", status: "normal", explanation: "Uric acid is within normal range here, compared to 7.5 (high) in April. Less intense exercise and more hydration during break explains this.", contextNote: "When you're back to intense training, make sure to increase water intake proportionally to keep uric acid in check." },
    { name: "Creatinine", value: "0.9 mg/dL", range: "0.7 - 1.3", status: "normal", explanation: "Kidneys are functioning perfectly.", contextNote: null },
    { name: "TSH", value: "2.8 µIU/mL", range: "0.4 - 4.0", status: "normal", explanation: "Thyroid function is normal.", contextNote: null },
    { name: "Calcium", value: "8.8 mg/dL", range: "8.5 - 10.5", status: "normal", explanation: "Calcium is on the lower side of normal. Combined with your very low Vitamin D, calcium absorption may be suboptimal.", contextNote: null },
  ],
  doctorFlags: [
    { issue: "Vitamin D remains critically low at 12.8 ng/mL — now persistent across two reports. Supplementation is strongly recommended. See a doctor for a prescription.", urgency: "soon" },
  ],
  lifestyle: [
    "Your January report is cleaner than April across the board — home food, better sleep, and lower training intensity made a visible difference.",
    "Focus on replicating home-style eating when possible at the hostel: more dal, curd, vegetables, and less fried food.",
    "Vitamin D supplementation is non-negotiable at this point — two reports showing significant deficiency.",
    "Your B12 improved with home food but is still marginal. A daily supplement would be a smart investment.",
    "The fact that your uric acid and SGPT normalized during break tells you these are lifestyle-driven, not medical issues. Sleep, hydration, and diet are your levers."
  ],
};

// ══════════════════════════════════════════════
// MAIN APP
// ══════════════════════════════════════════════
export default function SehaSaathiOS() {
  const [tab, setTab] = useState("dashboard");
  const [profile, setProfile] = useState({
    name: "Amrutanshu Dash", age: "20", sex: "Male", occupation: "Student", occupationDetail: "Dual Degree (B.Tech + M.Tech) Chemical Engineering, IIT Delhi",
    sleepHrs: "6", freeTimeHrs: "2", foodPref: "Non-Vegetarian", sports: ["Badminton", "Cricket", "Football", "Swimming", "Tennis", "Squash", "Table Tennis"],
    conditions: ["None"], medications: "", familyHistory: "", weight: "", height: "",
  });
  const [profileSaved, setProfileSaved] = useState(true);
  const [reports, setReports] = useState([DEMO_REPORT_1, DEMO_REPORT_2]);
  const [activeReport, setActiveReport] = useState(null);
  const [reportResult, setReportResult] = useState(null);
  const [savedResults] = useState({ 1001: SAVED_ANALYSIS_1, 1002: SAVED_ANALYSIS_2 });
  const [aiPlan, setAiPlan] = useState(null);
  const [loading, setLoading] = useState("");
  const [notifOpen, setNotifOpen] = useState(false);
  const fileRef = useRef(null);

  // Workout State
  const [workoutPhase, setWorkoutPhase] = useState("select"); // select | prep | active | complete
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [specialNotes, setSpecialNotes] = useState("");
  const [workoutExercises, setWorkoutExercises] = useState([]);
  const [currentExIdx, setCurrentExIdx] = useState(0);
  const [completedSets, setCompletedSets] = useState({});
  const [workoutTimer, setWorkoutTimer] = useState(0);
  const [workoutRunning, setWorkoutRunning] = useState(false);
  const [restTimer, setRestTimer] = useState(0);
  const [restActive, setRestActive] = useState(false);
  const [liveHR, setLiveHR] = useState(72);
  const [liveCals, setLiveCals] = useState(0);
  const [warmupAdvice, setWarmupAdvice] = useState("");
  const [considerations, setConsiderations] = useState("");
  const [cooldownAdvice, setCooldownAdvice] = useState("");
  const [workoutHistory, setWorkoutHistory] = useState([]);

  // Mood State
  const [moodLogs, setMoodLogs] = useState([
    { ts: Date.now() - 86400000*6, mood: 4, feelings: ["Calm","Focused"], note: "Good study session" },
    { ts: Date.now() - 86400000*5, mood: 3, feelings: ["Tired"], note: "Didn't sleep well" },
    { ts: Date.now() - 86400000*4, mood: 5, feelings: ["Energetic","Happy"], note: "Morning run felt amazing" },
    { ts: Date.now() - 86400000*3, mood: 2, feelings: ["Stressed","Anxious"], note: "Exam pressure" },
    { ts: Date.now() - 86400000*2, mood: 3, feelings: ["Okay"], note: "" },
    { ts: Date.now() - 86400000, mood: 4, feelings: ["Motivated","Grateful"], note: "Finished assignment" },
  ]);
  const [showMoodPrompt, setShowMoodPrompt] = useState(false);
  const [currentMood, setCurrentMood] = useState(null);
  const [currentFeelings, setCurrentFeelings] = useState([]);
  const [currentMoodNote, setCurrentMoodNote] = useState("");

  // Vitals navigation state
  const [vitalFilter, setVitalFilter] = useState(null); // which vital to highlight/scroll to
  const [vitalTimeRange, setVitalTimeRange] = useState("week"); // week | month | 3month
  const vitalRefs = useRef({});

  const today = MOCK_VITALS_WEEK[MOCK_VITALS_WEEK.length - 1];
  const unreadNotifs = MOCK_NOTIFICATIONS.filter(n => n.type === "warning" || n.type === "alert").length;

  // ── Timers ──
  useEffect(() => {
    if (!workoutRunning) return;
    const i = setInterval(() => setWorkoutTimer(t => t + 1), 1000);
    return () => clearInterval(i);
  }, [workoutRunning]);

  useEffect(() => {
    if (!restActive || restTimer <= 0) return;
    const i = setInterval(() => {
      setRestTimer(t => {
        if (t <= 1) { setRestActive(false); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(i);
  }, [restActive, restTimer]);

  // Simulate live HR during workout
  useEffect(() => {
    if (!workoutRunning) return;
    const i = setInterval(() => {
      setLiveHR(h => Math.min(180, Math.max(90, h + Math.floor(Math.random()*7) - 3)));
      setLiveCals(c => c + +(0.08 + Math.random()*0.12).toFixed(1));
    }, 2000);
    return () => clearInterval(i);
  }, [workoutRunning]);

  // Mood prompt timer (every 6-8 hours — checks every 30 min, shows once on load after 45s for demo)
  useEffect(() => {
    const i = setInterval(() => {
      const last = moodLogs[moodLogs.length - 1];
      if (!last || Date.now() - last.ts > 7 * 60 * 60 * 1000) {
        setShowMoodPrompt(true);
      }
    }, 30 * 60 * 1000);
    // Show once on first load after 45s for demo purposes
    const t = setTimeout(() => {
      const last = moodLogs[moodLogs.length - 1];
      if (!last || Date.now() - last.ts > 6 * 60 * 60 * 1000) {
        setShowMoodPrompt(true);
      }
    }, 45000);
    return () => { clearInterval(i); clearTimeout(t); };
  }, [moodLogs]);

  // Scroll to highlighted vital when navigating from dashboard
  useEffect(() => {
    if (tab === "vitals" && vitalFilter && vitalRefs.current[vitalFilter]) {
      setTimeout(() => {
        vitalRefs.current[vitalFilter]?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [tab, vitalFilter]);

  const navigateToVital = (vitalKey) => {
    setVitalFilter(vitalKey);
    setTab("vitals");
  };

  const fmt = (s) => `${Math.floor(s/60).toString().padStart(2,"0")}:${(s%60).toString().padStart(2,"0")}`;

  // ── PDF Extraction ──
  const loadPdfJs = () => new Promise((resolve, reject) => {
    if (window.pdfjsLib) return resolve(window.pdfjsLib);
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    s.onload = () => { window.pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js"; resolve(window.pdfjsLib); };
    s.onerror = () => reject(new Error("Could not load PDF.js"));
    document.head.appendChild(s);
  });
  const extractPDF = async (file) => {
    const lib = await loadPdfJs(); const buf = await file.arrayBuffer();
    const pdf = await lib.getDocument({ data: buf }).promise; let text = "";
    for (let i = 1; i <= pdf.numPages; i++) { const pg = await pdf.getPage(i); const ct = await pg.getTextContent(); text += ct.items.map(x => x.str).join(" ") + "\n"; }
    return text;
  };
  // Fallback: read file as text (works for text-based PDFs and txt files)
  const readAsText = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsText(file);
  });

  const [pasteMode, setPasteMode] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const handleReportUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    let text = "";
    try {
      text = await extractPDF(file);
    } catch {
      // Fallback: try reading as raw text
      try { text = await readAsText(file); } catch { /* will be empty */ }
    }
    if (!text || text.trim().length < 20) {
      // PDF couldn't be parsed — switch to paste mode
      setPasteMode(true);
      const report = { id: Date.now(), name: file.name, date: new Date().toLocaleDateString(), text: "", size: (file.size/1024).toFixed(0)+" KB", needsText: true };
      setReports(prev => [report, ...prev]); setActiveReport(report); setReportResult(null);
      return;
    }
    const report = { id: Date.now(), name: file.name, date: new Date().toLocaleDateString(), text, size: (file.size/1024).toFixed(0)+" KB" };
    setReports(prev => [report, ...prev]); setActiveReport(report); setReportResult(null);
  };

  const handlePasteSubmit = () => {
    if (!pasteText.trim() || !activeReport) return;
    const updated = { ...activeReport, text: pasteText.trim(), needsText: false };
    setActiveReport(updated);
    setReports(prev => prev.map(r => r.id === updated.id ? updated : r));
    setPasteMode(false); setPasteText("");
  };

  const handleManualReport = () => {
    if (!pasteText.trim()) return;
    const report = { id: Date.now(), name: "Pasted Report", date: new Date().toLocaleDateString(), text: pasteText.trim(), size: (pasteText.length / 1024).toFixed(0) + " KB" };
    setReports(prev => [report, ...prev]); setActiveReport(report); setReportResult(null);
    setPasteMode(false); setPasteText("");
  };

  // ── Gemini API (works when running locally with npm start) ──
  // In the Claude.ai artifact preview, API calls are blocked — demo fallbacks kick in automatically.
  const GEMINI_API_KEY = "YOUR_GEMINI_API_KEY_HERE";
  const callGemini = async (systemPrompt, userMessage) => {
    const res = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${GEMINI_API_KEY}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system_instruction: { parts: [{ text: systemPrompt }] },
          contents: [{ parts: [{ text: userMessage }] }],
          generationConfig: { temperature: 0.7, maxOutputTokens: 4000, responseMimeType: "application/json" },
        }),
      }
    );
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    const data = await res.json();
    const raw = data.candidates?.[0]?.content?.parts?.[0]?.text || "";
    if (!raw) throw new Error("Empty response");
    return JSON.parse(raw.replace(/```json|```/g, "").trim());
  };

  // ── Demo Fallback: AI Plan ──
  const DEMO_AI_PLAN = {
    dietPlan: {
      philosophy: "As an active 20-year-old athlete with 2 hours of free time daily, your diet needs to fuel intense sports sessions while compensating for hostel food limitations. Focus on protein timing, hydration, and adding fresh foods wherever the mess falls short.",
      meals: [
        { meal: "Breakfast", time: "7:30 AM", suggestion: "4 egg whites + 1 whole egg omelette with onions & tomatoes, 2 multigrain toast, 1 banana, glass of milk", calories: 520, notes: "Eat within 30 min of waking up. Eggs from the mess + fruit from the campus juice shop." },
        { meal: "Mid-Morning Snack", time: "10:30 AM", suggestion: "Handful of mixed nuts (almonds, walnuts) + 1 seasonal fruit", calories: 220, notes: "Keep a box of nuts in your hostel room. Quick fuel between classes." },
        { meal: "Lunch", time: "1:00 PM", suggestion: "2 chapati + dal + sabzi + curd + salad. Take double dal, skip the fried item.", calories: 650, notes: "The mess dal is your best protein source. Always add curd for B12 and gut health." },
        { meal: "Pre-Workout Snack", time: "4:30 PM", suggestion: "1 banana + handful of peanuts or a protein bar", calories: 250, notes: "Eat 45 min before your sports session for sustained energy." },
        { meal: "Dinner", time: "8:30 PM", suggestion: "2 chapati + chicken/paneer curry + sabzi + salad. Skip rice if having chapati.", calories: 600, notes: "If mess chicken is too oily, ask for the tandoori option. Post-workout protein is crucial." },
      ],
    },
    trainingPlan: {
      philosophy: "With your diverse sports background and 2 hours of daily free time, we'll alternate between sports-specific sessions and functional fitness. Your Vitamin D deficiency means outdoor sessions whenever possible. High uric acid means extra hydration around workouts.",
      weeklySchedule: [
        { day: "Mon", activity: "Badminton (competitive games)", duration: "1.5 hrs", intensity: "high", notes: "Campus courts. Hydrate with 1L water during play." },
        { day: "Tue", activity: "Gym — Upper Body + Core", duration: "1 hr", intensity: "moderate", notes: "Push-ups, rows, shoulder press, planks. Keep it functional, not bodybuilding." },
        { day: "Wed", activity: "Swimming", duration: "1 hr", intensity: "moderate", notes: "IIT pool. Great cardio with zero joint stress. Helps with your squash recovery." },
        { day: "Thu", activity: "Squash / Table Tennis", duration: "1.5 hrs", intensity: "high", notes: "Alternate weeks. These sessions build your reflexes and agility." },
        { day: "Fri", activity: "Gym — Lower Body + Flexibility", duration: "1 hr", intensity: "moderate", notes: "Squats, lunges, calf raises, 15 min stretching. Flexibility prevents sports injuries." },
        { day: "Sat", activity: "Football / Cricket with friends", duration: "1.5-2 hrs", intensity: "high", notes: "Weekend group sports. Social + fitness. Stay hydrated — 3L minimum today." },
        { day: "Sun", activity: "Active Recovery — Yoga or Walk", duration: "30-45 min", intensity: "low", notes: "Light yoga in the hostel courtyard for sun exposure. Helps Vitamin D and recovery." },
      ],
    },
    sleepAdvice: "You're averaging 6 hours — you need at least 7 for proper muscle recovery and cognitive function. Set a hard 12:30 AM sleep deadline on weekdays. Your SGPT elevation correlates with sleep deprivation. Try the 10-3-2-1 rule: no caffeine 10 hrs before bed, no heavy food 3 hrs, no work 2 hrs, no screens 1 hr.",
    keyInsights: [
      "Your elevated uric acid (7.5) directly correlates with intense sports + protein intake. Drink 3-4 litres of water daily, with at least 500ml in the 2 hours after every workout.",
      "Vitamin D at 16.2 is critically low. Sunday morning yoga in sunlight helps, but you need a prescribed 60,000 IU weekly supplement for 8 weeks. This single fix will improve your energy, immunity, and calcium absorption.",
      "Your triglycerides went from 148 (home) to 162 (hostel) — the difference is purely dietary. Swapping the fried mess item for extra dal + curd at dinner can reverse this within 4-6 weeks.",
      "B12 is borderline. A daily 500mcg supplement costs ₹3/day and prevents fatigue, brain fog, and nerve issues. Non-negotiable for hostel life.",
      "Your January report proves your body responds fast to lifestyle changes — cleaner food and better sleep normalized your SGPT and uric acid within weeks. You have strong fundamentals; small optimizations will make a big difference."
    ],
  };

  // ── Demo Fallback: Generic uploaded report analysis ──
  const DEMO_UPLOADED_ANALYSIS = {
    summary: "Report uploaded successfully and analyzed. The parameters have been categorized below based on the reference ranges provided. For the most accurate personalized insights, please run this app locally with your Gemini API key configured.",
    parameters: [
      { name: "Sample Parameter", value: "—", range: "—", status: "normal", explanation: "To see full AI analysis of uploaded reports, run the app locally with your Gemini API key. The demo reports (Apr 2026 and Jan 2026) have pre-built analysis — click those to see the full experience.", contextNote: null },
    ],
    doctorFlags: [],
    lifestyle: ["For the full AI-powered analysis experience, run the app locally using 'npm start' with your Gemini API key configured in the code. The two demo reports have complete pre-built analysis available."],
  };

  const analyzeReport = async () => {
    if (!activeReport) return;
    // Check if we have a saved analysis first
    if (savedResults[activeReport.id]) {
      setReportResult(savedResults[activeReport.id]);
      return;
    }
    setLoading("report");
    try {
      const ctx = profileSaved ? `\nUser: Age ${profile.age}, Sex: ${profile.sex}, Conditions: ${profile.conditions.join(", ")}, Meds: ${profile.medications}, Family: ${profile.familyHistory}` : "";
      const result = await callGemini(SYSTEM_PROMPT_REPORT, `Report:\n${activeReport.text}${ctx}`);
      setReportResult(result);
    } catch (e) {
      console.error("API call failed, using demo fallback:", e);
      // Simulate loading for demo feel
      await new Promise(r => setTimeout(r, 1500));
      setReportResult(DEMO_UPLOADED_ANALYSIS);
    }
    setLoading("");
  };

  const generatePlan = async () => {
    if (!profileSaved) { alert("Save profile first!"); setTab("profile"); return; }
    setLoading("plan");
    try {
      const msg = `Profile: ${profile.name}, Age ${profile.age}, ${profile.sex}, ${profile.occupation} ${profile.occupationDetail}, ${profile.weight}kg/${profile.height}cm, Free time: ${profile.freeTimeHrs}hrs/day, Sleep: ${profile.sleepHrs}hrs, Food: ${profile.foodPref}, Sports: ${profile.sports.join(",")||"None"}, Conditions: ${profile.conditions.join(",")||"None"}, Meds: ${profile.medications||"None"}, Family: ${profile.familyHistory||"None"}. Avg HR: ${Math.round(MOCK_VITALS_WEEK.reduce((s,d)=>s+d.hr,0)/7)}, Avg sleep: ${(MOCK_VITALS_WEEK.reduce((s,d)=>s+d.sleep,0)/7).toFixed(1)}hrs`;
      setAiPlan(await callGemini(SYSTEM_PROMPT_PLAN, msg));
    } catch (e) {
      console.error("API call failed, using demo plan:", e);
      await new Promise(r => setTimeout(r, 2000));
      setAiPlan(DEMO_AI_PLAN);
    }
    setLoading("");
  };

  const startWorkoutAI = async () => {
    setLoading("workout");
    try {
      const exercises = WORKOUT_TEMPLATES[selectedWorkout] || [];
      const msg = `Workout type: ${selectedWorkout}\nExercises: ${JSON.stringify(exercises)}\nSpecial considerations: ${specialNotes || "None"}\nUser profile: Age ${profile.age||"unknown"}, Conditions: ${profile.conditions?.join(",")||"None"}`;
      const result = await callGemini(SYSTEM_PROMPT_WORKOUT, msg);
      setWorkoutExercises(result.exercises || exercises);
      setWarmupAdvice(result.warmupAdvice || "");
      setConsiderations(result.considerations || "");
      setCooldownAdvice(result.cooldownAdvice || "");
      setWorkoutPhase("active");
      setCurrentExIdx(0);
      setCompletedSets({});
      setWorkoutTimer(0);
      setLiveHR(85);
      setLiveCals(0);
      setWorkoutRunning(true);
    } catch { 
      // Fallback to template
      setWorkoutExercises(WORKOUT_TEMPLATES[selectedWorkout] || []);
      setWorkoutPhase("active");
      setWorkoutRunning(true);
    }
    setLoading("");
  };

  const completeSet = (exIdx, setNum) => {
    setCompletedSets(prev => ({ ...prev, [`${exIdx}-${setNum}`]: true }));
    const ex = workoutExercises[exIdx];
    if (ex?.rest > 0) { setRestTimer(ex.rest); setRestActive(true); }
  };

  const finishWorkout = () => {
    setWorkoutRunning(false);
    setWorkoutPhase("complete");
    setWorkoutHistory(prev => [...prev, { date: new Date().toLocaleDateString(), type: selectedWorkout, duration: workoutTimer, calories: Math.round(liveCals), avgHR: liveHR }]);
  };

  const resetWorkout = () => {
    setWorkoutPhase("select"); setSelectedWorkout(null); setSpecialNotes(""); setWorkoutExercises([]);
    setCurrentExIdx(0); setCompletedSets({}); setWorkoutTimer(0); setWorkoutRunning(false);
    setRestTimer(0); setRestActive(false); setLiveHR(72); setLiveCals(0);
    setWarmupAdvice(""); setConsiderations(""); setCooldownAdvice("");
  };

  const submitMood = () => {
    if (currentMood === null) return;
    setMoodLogs(prev => [...prev, { ts: Date.now(), mood: currentMood, feelings: currentFeelings, note: currentMoodNote }]);
    setShowMoodPrompt(false); setCurrentMood(null); setCurrentFeelings([]); setCurrentMoodNote("");
  };

  // ── Theme ──
  const c = {
    bg: "#0a0f1a", surface: "#111827", surfaceLight: "#1a2234", border: "#1e293b",
    accent: "#34d399", accentDim: "rgba(52,211,153,0.15)", accentGlow: "rgba(52,211,153,0.3)",
    text: "#e2e8f0", textDim: "#94a3b8", textMuted: "#64748b",
    danger: "#f87171", warning: "#fbbf24", info: "#60a5fa",
  };
  const cardStyle = { background: c.surface, border: `1px solid ${c.border}`, borderRadius: 16, padding: 20 };
  const btnPrimary = { padding: "12px 24px", background: "linear-gradient(135deg, #059669, #34d399)", color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer" };
  const inputStyle = { width: "100%", padding: "10px 14px", background: c.surfaceLight, border: `1px solid ${c.border}`, borderRadius: 10, color: c.text, fontSize: 14, outline: "none", fontFamily: "inherit" };
  const chipStyle = (active) => ({ padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer", border: `1.5px solid ${active ? c.accent : c.border}`, background: active ? c.accentDim : "transparent", color: active ? c.accent : c.textDim, transition: "all 0.15s" });

  const MetricCard = ({ icon, label, value, unit, trend, color = c.accent, vitalKey }) => (
    <div onClick={() => vitalKey && navigateToVital(vitalKey)} style={{ ...cardStyle, padding: 16, flex: "1 1 140px", minWidth: 140, cursor: vitalKey ? "pointer" : "default", transition: "all 0.2s", position: "relative", overflow: "hidden" }}
      onMouseEnter={e => { if(vitalKey) { e.currentTarget.style.borderColor = color; e.currentTarget.style.transform = "translateY(-2px)"; }}}
      onMouseLeave={e => { if(vitalKey) { e.currentTarget.style.borderColor = c.border; e.currentTarget.style.transform = "translateY(0)"; }}}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 22 }}>{icon}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {trend && <span style={{ fontSize: 11, color: trend > 0 ? c.danger : c.accent }}>{trend > 0 ? "↑" : "↓"}{Math.abs(trend)}%</span>}
          {vitalKey && <span style={{ fontSize: 10, color: c.textMuted }}>→</span>}
        </div>
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1 }}>{value}<span style={{ fontSize: 13, fontWeight: 400, color: c.textMuted, marginLeft: 3 }}>{unit}</span></div>
      <div style={{ fontSize: 11, color: c.textMuted, marginTop: 4, textTransform: "uppercase", letterSpacing: 1 }}>{label}</div>
    </div>
  );

  // ── Mood Chart Data ──
  const moodChartData = moodLogs.slice(-7).map(m => ({
    day: new Date(m.ts).toLocaleDateString("en", { weekday: "short" }),
    mood: m.mood,
  }));

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Fraunces:opsz,wght@9..144,600;9..144,700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 5px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 3px; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:translateY(0) } }
        @keyframes pulse { 0%,100% { transform:scale(1) } 50% { transform:scale(1.05) } }
        @keyframes spin { to { transform:rotate(360deg) } }
        @keyframes glow { 0%,100% { box-shadow: 0 0 20px rgba(52,211,153,0.15) } 50% { box-shadow: 0 0 40px rgba(52,211,153,0.3) } }
        @keyframes slideIn { from { opacity:0; transform:translateY(-20px) } to { opacity:1; transform:translateY(0) } }
        @keyframes breathe { 0%,100% { opacity:0.6 } 50% { opacity:1 } }
      `}</style>

      <div style={{ fontFamily: "'Outfit', sans-serif", background: c.bg, color: c.text, minHeight: "100vh", display: "flex", flexDirection: "column" }}>

        {/* ═══ MOOD PROMPT OVERLAY ═══ */}
        {showMoodPrompt && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, animation: "fadeUp 0.3s ease", backdropFilter: "blur(8px)" }}>
            <div style={{ background: c.surface, borderRadius: 20, padding: 28, maxWidth: 400, width: "100%", border: `1px solid ${c.border}`, animation: "slideIn 0.4s ease" }}>
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>🫶</div>
                <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 700, marginBottom: 4 }}>How are you feeling?</h3>
                <p style={{ fontSize: 13, color: c.textMuted }}>Take a moment to check in with yourself</p>
              </div>

              <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 20 }}>
                {MOOD_OPTIONS.map(m => (
                  <button key={m.value} onClick={() => setCurrentMood(m.value)} style={{
                    width: 56, height: 56, borderRadius: 14, border: `2px solid ${currentMood === m.value ? m.color : c.border}`,
                    background: currentMood === m.value ? `${m.color}20` : "transparent",
                    cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", transition: "all 0.2s",
                    transform: currentMood === m.value ? "scale(1.1)" : "scale(1)",
                  }}>
                    <span style={{ fontSize: 24 }}>{m.emoji}</span>
                    <span style={{ fontSize: 9, color: currentMood === m.value ? m.color : c.textMuted, fontWeight: 600 }}>{m.label}</span>
                  </button>
                ))}
              </div>

              <p style={{ fontSize: 12, color: c.textMuted, marginBottom: 8 }}>What describes your state? (optional)</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                {FEELING_TAGS.map(f => (
                  <span key={f} onClick={() => setCurrentFeelings(prev => prev.includes(f) ? prev.filter(x=>x!==f) : [...prev, f])}
                    style={chipStyle(currentFeelings.includes(f))}>{f}</span>
                ))}
              </div>

              <textarea value={currentMoodNote} onChange={e => setCurrentMoodNote(e.target.value)}
                placeholder="Anything on your mind? (optional)" rows={2}
                style={{ ...inputStyle, resize: "none", marginBottom: 16 }} />

              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => { setShowMoodPrompt(false); setCurrentMood(null); setCurrentFeelings([]); setCurrentMoodNote(""); }}
                  style={{ flex: 1, padding: 12, background: "transparent", border: `1px solid ${c.border}`, borderRadius: 12, color: c.textDim, cursor: "pointer", fontSize: 14 }}>
                  Skip
                </button>
                <button onClick={submitMood} disabled={currentMood === null}
                  style={{ ...btnPrimary, flex: 2, opacity: currentMood === null ? 0.4 : 1, padding: 12 }}>
                  Log Feeling
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ HEADER ═══ */}
        <header style={{ padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${c.border}`, background: "rgba(10,15,26,0.95)", backdropFilter: "blur(20px)", position: "sticky", top: 0, zIndex: 100 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg, #059669, #34d399)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, animation: "glow 3s ease-in-out infinite" }}>🩺</div>
            <div>
              <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 700, color: c.accent, lineHeight: 1.1 }}>Sehat Saathi</h1>
              <p style={{ fontSize: 10, color: c.textMuted, letterSpacing: 2, textTransform: "uppercase" }}>Health OS</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setShowMoodPrompt(true)} style={{ background: "transparent", border: `1px solid ${c.border}`, borderRadius: 10, padding: "8px 10px", cursor: "pointer", color: c.text, fontSize: 16 }}>🫶</button>
            <button onClick={() => setNotifOpen(!notifOpen)} style={{ position: "relative", background: "transparent", border: `1px solid ${c.border}`, borderRadius: 10, padding: "8px 10px", cursor: "pointer", color: c.text, fontSize: 16 }}>
              🔔{unreadNotifs > 0 && <span style={{ position: "absolute", top: -4, right: -4, width: 18, height: 18, borderRadius: "50%", background: c.danger, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{unreadNotifs}</span>}
            </button>
          </div>
        </header>

        {/* Notification Panel */}
        {notifOpen && (
          <div style={{ position: "fixed", top: 60, right: 16, width: 360, maxHeight: "70vh", overflowY: "auto", background: c.surface, border: `1px solid ${c.border}`, borderRadius: 16, padding: 16, zIndex: 200, animation: "fadeUp 0.2s ease", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Notifications</h3>
            {MOCK_NOTIFICATIONS.map(n => (
              <div key={n.id} style={{ padding: 14, borderRadius: 12, marginBottom: 8, background: n.type==="alert"?"rgba(248,113,113,0.08)":n.type==="warning"?"rgba(251,191,36,0.08)":"rgba(96,165,250,0.08)", border: `1px solid ${n.type==="alert"?"rgba(248,113,113,0.2)":n.type==="warning"?"rgba(251,191,36,0.2)":"rgba(96,165,250,0.2)"}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{n.icon} {n.title}</span>
                  <span style={{ fontSize: 10, color: c.textMuted }}>{n.time}</span>
                </div>
                <p style={{ fontSize: 12, color: c.textDim, lineHeight: 1.5 }}>{n.body}</p>
              </div>
            ))}
          </div>
        )}

        {/* ═══ MAIN CONTENT ═══ */}
        <main style={{ flex: 1, padding: "20px 20px 120px", maxWidth: 800, margin: "0 auto", width: "100%" }}>

          {/* ── DASHBOARD ── */}
          {tab === "dashboard" && (
            <div style={{ animation: "fadeUp 0.3s ease" }}>
              <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 700, marginBottom: 4 }}>{profile.name ? `Hey ${profile.name.split(" ")[0]}` : "Your Dashboard"} 👋</h2>
              <p style={{ color: c.textDim, fontSize: 14, marginBottom: 20 }}>Health snapshot for today</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
                <MetricCard icon="💓" label="Heart Rate" value={today.hr} unit="bpm" trend={3} color={today.hr>80?c.warning:c.accent} vitalKey="hr" />
                <MetricCard icon="🫁" label="SpO2" value={today.spo2} unit="%" color={today.spo2<95?c.danger:c.accent} vitalKey="spo2" />
                <MetricCard icon="🌙" label="Sleep" value={today.sleep} unit="hrs" trend={-8} color={today.sleep<6?c.warning:c.accent} vitalKey="sleep" />
                <MetricCard icon="🏃" label="Steps" value={today.steps.toLocaleString()} unit="" color={c.accent} vitalKey="steps" />
                <MetricCard icon="🌡️" label="Temp" value={today.temp} unit="°C" color={c.text} vitalKey="temp" />
                <MetricCard icon="🔥" label="Calories" value={today.calories} unit="kcal" color={c.info} vitalKey="calories" />
              </div>

              {/* Mood Summary */}
              <div style={{ ...cardStyle, marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, color: c.textDim }}>Mood · 7 Day Trend</h3>
                  <button onClick={() => setShowMoodPrompt(true)} style={{ fontSize: 12, color: c.accent, background: "transparent", border: "none", cursor: "pointer", fontWeight: 600 }}>+ Log Now</button>
                </div>
                <div style={{ display: "flex", justifyContent: "space-around", alignItems: "flex-end", height: 80 }}>
                  {moodChartData.map((d, i) => {
                    const mo = MOOD_OPTIONS.find(m => m.value === d.mood);
                    return (
                      <div key={i} style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 24, marginBottom: 4, transition: "transform 0.2s" }}>{mo?.emoji}</div>
                        <div style={{ width: 32, height: d.mood * 10, borderRadius: 6, background: `${mo?.color}40`, border: `1px solid ${mo?.color}60`, transition: "height 0.3s" }} />
                        <div style={{ fontSize: 10, color: c.textMuted, marginTop: 4 }}>{d.day}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* HR Chart */}
              <div style={{ ...cardStyle, marginBottom: 16 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: c.textDim }}>Heart Rate · 7 Day Trend</h3>
                <ResponsiveContainer width="100%" height={160}>
                  <AreaChart data={MOCK_VITALS_WEEK}>
                    <defs><linearGradient id="hrGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#34d399" stopOpacity={0.3}/><stop offset="100%" stopColor="#34d399" stopOpacity={0}/></linearGradient></defs>
                    <XAxis dataKey="day" stroke={c.textMuted} fontSize={11} tickLine={false} axisLine={false}/>
                    <YAxis domain={[60,90]} stroke={c.textMuted} fontSize={11} tickLine={false} axisLine={false} width={30}/>
                    <Tooltip contentStyle={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 10, fontSize: 12 }}/>
                    <Area type="monotone" dataKey="hr" stroke="#34d399" fill="url(#hrGrad)" strokeWidth={2.5} dot={{ r: 3, fill: "#34d399" }}/>
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={{ ...cardStyle, flex: 1, minWidth: 240 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: c.textDim }}>Sleep</h3>
                  <ResponsiveContainer width="100%" height={120}>
                    <BarChart data={MOCK_VITALS_WEEK}><XAxis dataKey="day" stroke={c.textMuted} fontSize={11} tickLine={false} axisLine={false}/><YAxis domain={[0,10]} stroke={c.textMuted} fontSize={11} tickLine={false} axisLine={false} width={25}/><Tooltip contentStyle={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 10, fontSize: 12 }}/><Bar dataKey="sleep" fill="#818cf8" radius={[4,4,0,0]}/></BarChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ ...cardStyle, flex: 1, minWidth: 240 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: c.textDim }}>Steps</h3>
                  <ResponsiveContainer width="100%" height={120}>
                    <BarChart data={MOCK_VITALS_WEEK}><XAxis dataKey="day" stroke={c.textMuted} fontSize={11} tickLine={false} axisLine={false}/><YAxis stroke={c.textMuted} fontSize={11} tickLine={false} axisLine={false} width={35}/><Tooltip contentStyle={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 10, fontSize: 12 }}/><Bar dataKey="steps" fill="#34d399" radius={[4,4,0,0]}/></BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* ── WORKOUT TAB ── */}
          {tab === "workout" && (
            <div style={{ animation: "fadeUp 0.3s ease" }}>
              
              {/* SELECT PHASE */}
              {workoutPhase === "select" && (
                <>
                  <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Start Workout 💪</h2>
                  <p style={{ color: c.textDim, fontSize: 14, marginBottom: 20 }}>Choose a workout and we'll track your vitals live</p>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
                    {Object.keys(WORKOUT_TEMPLATES).map(w => (
                      <div key={w} onClick={() => setSelectedWorkout(w)} style={{
                        ...cardStyle, padding: 18, cursor: "pointer", textAlign: "center",
                        borderColor: selectedWorkout === w ? c.accent : c.border,
                        background: selectedWorkout === w ? c.accentDim : c.surface,
                        transition: "all 0.2s",
                      }}>
                        <div style={{ fontSize: 28, marginBottom: 6 }}>
                          {w === "Upper Body" ? "💪" : w === "Lower Body" ? "🦵" : w === "Cardio & Core" ? "🏃" : w === "Yoga Flow" ? "🧘" : "⚡"}
                        </div>
                        <p style={{ fontWeight: 600, fontSize: 14 }}>{w}</p>
                        <p style={{ fontSize: 11, color: c.textMuted, marginTop: 4 }}>{WORKOUT_TEMPLATES[w].length} exercises</p>
                      </div>
                    ))}
                  </div>

                  {selectedWorkout && (
                    <>
                      <div style={{ ...cardStyle, marginBottom: 16 }}>
                        <h4 style={{ fontSize: 13, fontWeight: 600, color: c.warning, marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>⚠️ Any considerations today?</h4>
                        <p style={{ fontSize: 12, color: c.textMuted, marginBottom: 10 }}>Tell us about injuries, pain, or limitations. AI will modify your workout accordingly.</p>
                        <textarea value={specialNotes} onChange={e => setSpecialNotes(e.target.value)}
                          placeholder="e.g., My lower back hurts today, skip heavy deadlifts. Right knee is sore."
                          rows={3} style={{ ...inputStyle, resize: "none" }} />
                      </div>

                      <div style={{ ...cardStyle, marginBottom: 16 }}>
                        <h4 style={{ fontSize: 13, fontWeight: 600, color: c.textDim, marginBottom: 10 }}>Exercises Preview</h4>
                        {WORKOUT_TEMPLATES[selectedWorkout].map((ex, i) => (
                          <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < WORKOUT_TEMPLATES[selectedWorkout].length-1 ? `1px solid ${c.border}` : "none" }}>
                            <div>
                              <span style={{ fontWeight: 500, fontSize: 14 }}>{ex.name}</span>
                              <span style={{ fontSize: 11, color: c.textMuted, marginLeft: 8 }}>{ex.muscle}</span>
                            </div>
                            <span style={{ fontSize: 12, color: c.textDim }}>{ex.sets}×{ex.reps}</span>
                          </div>
                        ))}
                      </div>

                      <button onClick={startWorkoutAI} disabled={loading === "workout"} style={{ ...btnPrimary, width: "100%", padding: 16, fontSize: 16 }}>
                        {loading === "workout" ? "Preparing your workout..." : "🏋️ Begin Workout"}
                      </button>
                    </>
                  )}

                  {/* Workout History */}
                  {workoutHistory.length > 0 && (
                    <div style={{ marginTop: 24 }}>
                      <h4 style={{ fontSize: 13, fontWeight: 600, color: c.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>Recent Workouts</h4>
                      {workoutHistory.slice(-5).reverse().map((w, i) => (
                        <div key={i} style={{ ...cardStyle, padding: 14, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <p style={{ fontWeight: 600, fontSize: 14 }}>{w.type}</p>
                            <p style={{ fontSize: 11, color: c.textMuted }}>{w.date}</p>
                          </div>
                          <div style={{ display: "flex", gap: 16, fontSize: 13 }}>
                            <span>⏱ {fmt(w.duration)}</span>
                            <span>🔥 {w.calories} kcal</span>
                            <span>💓 {w.avgHR} bpm</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* ACTIVE WORKOUT */}
              {workoutPhase === "active" && (
                <>
                  {/* Live Stats Bar */}
                  <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                    <div style={{ ...cardStyle, flex: 1, padding: 14, textAlign: "center", background: "rgba(248,113,113,0.08)", borderColor: "rgba(248,113,113,0.2)" }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: c.danger, animation: "breathe 2s infinite" }}>{liveHR}</div>
                      <div style={{ fontSize: 10, color: c.textMuted, textTransform: "uppercase" }}>💓 Live HR</div>
                    </div>
                    <div style={{ ...cardStyle, flex: 1, padding: 14, textAlign: "center" }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: c.accent }}>{fmt(workoutTimer)}</div>
                      <div style={{ fontSize: 10, color: c.textMuted, textTransform: "uppercase" }}>⏱ Duration</div>
                    </div>
                    <div style={{ ...cardStyle, flex: 1, padding: 14, textAlign: "center", background: "rgba(251,191,36,0.08)", borderColor: "rgba(251,191,36,0.2)" }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: c.warning }}>{Math.round(liveCals)}</div>
                      <div style={{ fontSize: 10, color: c.textMuted, textTransform: "uppercase" }}>🔥 Calories</div>
                    </div>
                  </div>

                  {/* Rest Timer */}
                  {restActive && (
                    <div style={{ ...cardStyle, textAlign: "center", padding: 20, marginBottom: 16, background: "rgba(96,165,250,0.08)", borderColor: "rgba(96,165,250,0.3)", animation: "pulse 2s infinite" }}>
                      <p style={{ fontSize: 13, color: c.info, fontWeight: 600, marginBottom: 4 }}>REST</p>
                      <p style={{ fontSize: 40, fontWeight: 800, color: c.info }}>{restTimer}s</p>
                      <button onClick={() => { setRestActive(false); setRestTimer(0); }} style={{ marginTop: 8, padding: "6px 20px", background: "transparent", border: `1px solid ${c.info}`, borderRadius: 8, color: c.info, cursor: "pointer", fontSize: 12 }}>Skip Rest</button>
                    </div>
                  )}

                  {/* Considerations */}
                  {considerations && (
                    <div style={{ ...cardStyle, padding: 14, marginBottom: 12, background: "rgba(251,191,36,0.06)", borderColor: "rgba(251,191,36,0.2)" }}>
                      <p style={{ fontSize: 12, color: c.warning, fontWeight: 600, marginBottom: 4 }}>⚠️ Today's Considerations</p>
                      <p style={{ fontSize: 12, color: c.textDim, lineHeight: 1.5 }}>{considerations}</p>
                    </div>
                  )}

                  {warmupAdvice && !completedSets["warmup"] && (
                    <div style={{ ...cardStyle, padding: 16, marginBottom: 12, background: "rgba(52,211,153,0.06)", borderColor: "rgba(52,211,153,0.2)" }}>
                      <p style={{ fontSize: 13, color: c.accent, fontWeight: 600, marginBottom: 6 }}>🔥 Warm Up First</p>
                      <p style={{ fontSize: 13, color: c.textDim, lineHeight: 1.6 }}>{warmupAdvice}</p>
                      <button onClick={() => setCompletedSets(p => ({...p, warmup: true}))} style={{ ...btnPrimary, marginTop: 10, padding: "8px 20px", fontSize: 13 }}>✓ Warmup Done</button>
                    </div>
                  )}

                  {/* Exercise List */}
                  <div style={{ marginBottom: 16 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: c.textDim, marginBottom: 12 }}>
                      Exercises ({workoutExercises.filter((_, i) => Array.from({length: workoutExercises[i]?.sets || 3}, (_, s) => completedSets[`${i}-${s}`]).every(Boolean)).length}/{workoutExercises.length} complete)
                    </h3>
                    {workoutExercises.map((ex, i) => {
                      const allDone = Array.from({length: ex.sets}, (_, s) => completedSets[`${i}-${s}`]).every(Boolean);
                      const isActive = i === currentExIdx;
                      return (
                        <div key={i} onClick={() => setCurrentExIdx(i)} style={{
                          ...cardStyle, padding: 16, marginBottom: 8, cursor: "pointer",
                          borderColor: isActive ? c.accent : allDone ? "rgba(74,222,128,0.3)" : c.border,
                          background: allDone ? "rgba(74,222,128,0.05)" : isActive ? c.accentDim : c.surface,
                          opacity: allDone ? 0.7 : 1,
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isActive ? 10 : 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <span style={{ fontSize: 18 }}>{allDone ? "✅" : isActive ? "🏋️" : "⬜"}</span>
                              <div>
                                <span style={{ fontWeight: 600, fontSize: 14, textDecoration: allDone ? "line-through" : "none" }}>{ex.name}</span>
                                {ex.modified && <span style={{ fontSize: 10, color: c.warning, marginLeft: 8, fontWeight: 600 }}>MODIFIED</span>}
                                <span style={{ fontSize: 11, color: c.textMuted, marginLeft: 8 }}>{ex.muscle}</span>
                              </div>
                            </div>
                            <span style={{ fontSize: 12, color: c.textDim }}>{ex.sets}×{ex.reps}</span>
                          </div>
                          {ex.modNote && isActive && (
                            <p style={{ fontSize: 11, color: c.warning, marginBottom: 8, padding: "6px 10px", background: "rgba(251,191,36,0.08)", borderRadius: 8 }}>💡 {ex.modNote}</p>
                          )}
                          {isActive && !allDone && (
                            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                              {Array.from({ length: ex.sets }, (_, s) => (
                                <button key={s} onClick={(e) => { e.stopPropagation(); completeSet(i, s); }}
                                  disabled={completedSets[`${i}-${s}`]}
                                  style={{
                                    flex: 1, padding: "8px 0", borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: completedSets[`${i}-${s}`] ? "default" : "pointer",
                                    background: completedSets[`${i}-${s}`] ? c.accent : "transparent",
                                    color: completedSets[`${i}-${s}`] ? "#fff" : c.accent,
                                    border: `1.5px solid ${c.accent}`, transition: "all 0.2s",
                                  }}>
                                  {completedSets[`${i}-${s}`] ? "✓" : `Set ${s+1}`}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {cooldownAdvice && (
                    <div style={{ ...cardStyle, padding: 14, marginBottom: 12, background: "rgba(129,140,248,0.06)", borderColor: "rgba(129,140,248,0.2)" }}>
                      <p style={{ fontSize: 13, color: "#818cf8", fontWeight: 600, marginBottom: 4 }}>🧊 Cool Down</p>
                      <p style={{ fontSize: 12, color: c.textDim, lineHeight: 1.5 }}>{cooldownAdvice}</p>
                    </div>
                  )}

                  <button onClick={finishWorkout} style={{ ...btnPrimary, width: "100%", padding: 16, fontSize: 15, background: "linear-gradient(135deg, #dc2626, #f87171)" }}>
                    🏁 Finish Workout
                  </button>
                </>
              )}

              {/* COMPLETE PHASE */}
              {workoutPhase === "complete" && (
                <div style={{ textAlign: "center", animation: "fadeUp 0.3s ease" }}>
                  <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
                  <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 700, marginBottom: 8 }}>Workout Complete!</h2>
                  <p style={{ color: c.textDim, fontSize: 14, marginBottom: 24 }}>Great job pushing through today</p>

                  <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 24 }}>
                    <div style={{ ...cardStyle, padding: 20, minWidth: 100, textAlign: "center" }}>
                      <div style={{ fontSize: 24, fontWeight: 700, color: c.accent }}>{fmt(workoutTimer)}</div>
                      <div style={{ fontSize: 11, color: c.textMuted }}>Duration</div>
                    </div>
                    <div style={{ ...cardStyle, padding: 20, minWidth: 100, textAlign: "center" }}>
                      <div style={{ fontSize: 24, fontWeight: 700, color: c.warning }}>{Math.round(liveCals)}</div>
                      <div style={{ fontSize: 11, color: c.textMuted }}>Calories</div>
                    </div>
                    <div style={{ ...cardStyle, padding: 20, minWidth: 100, textAlign: "center" }}>
                      <div style={{ fontSize: 24, fontWeight: 700, color: c.danger }}>{liveHR}</div>
                      <div style={{ fontSize: 11, color: c.textMuted }}>Peak HR</div>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={resetWorkout} style={{ ...btnPrimary, flex: 1 }}>New Workout</button>
                    <button onClick={() => setTab("dashboard")} style={{ flex: 1, padding: 12, background: "transparent", border: `1px solid ${c.border}`, borderRadius: 12, color: c.textDim, cursor: "pointer", fontSize: 14 }}>Dashboard</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── MOOD TAB ── */}
          {tab === "mood" && (
            <div style={{ animation: "fadeUp 0.3s ease" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 700 }}>Mental Wellness 🧠</h2>
                  <p style={{ color: c.textDim, fontSize: 14 }}>Track how you're feeling over time</p>
                </div>
                <button onClick={() => setShowMoodPrompt(true)} style={btnPrimary}>+ Check In</button>
              </div>

              {/* Mood Trend */}
              <div style={{ ...cardStyle, marginBottom: 16 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: c.textDim, marginBottom: 14 }}>Mood Trend</h3>
                <ResponsiveContainer width="100%" height={160}>
                  <AreaChart data={moodChartData}>
                    <defs><linearGradient id="moodGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#a78bfa" stopOpacity={0.3}/><stop offset="100%" stopColor="#a78bfa" stopOpacity={0}/></linearGradient></defs>
                    <XAxis dataKey="day" stroke={c.textMuted} fontSize={11} tickLine={false} axisLine={false}/>
                    <YAxis domain={[0,5]} ticks={[1,2,3,4,5]} stroke={c.textMuted} fontSize={11} tickLine={false} axisLine={false} width={25}/>
                    <Tooltip contentStyle={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 10, fontSize: 12 }} formatter={(v) => [MOOD_OPTIONS.find(m=>m.value===v)?.label, "Mood"]}/>
                    <Area type="monotone" dataKey="mood" stroke="#a78bfa" fill="url(#moodGrad)" strokeWidth={2.5} dot={{ r: 4, fill: "#a78bfa" }}/>
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Average Mood */}
              <div style={{ ...cardStyle, marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ fontSize: 40 }}>{MOOD_OPTIONS.find(m => m.value === Math.round(moodLogs.reduce((s,l) => s+l.mood,0)/moodLogs.length))?.emoji}</div>
                <div>
                  <p style={{ fontWeight: 600, fontSize: 16 }}>Average Mood: {(moodLogs.reduce((s,l)=>s+l.mood,0)/moodLogs.length).toFixed(1)} / 5</p>
                  <p style={{ fontSize: 12, color: c.textMuted }}>{moodLogs.length} check-ins logged</p>
                </div>
              </div>

              {/* Mood Log History */}
              <h4 style={{ fontSize: 13, fontWeight: 600, color: c.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>Recent Check-ins</h4>
              {[...moodLogs].reverse().slice(0, 10).map((log, i) => {
                const mo = MOOD_OPTIONS.find(m => m.value === log.mood);
                return (
                  <div key={i} style={{ ...cardStyle, padding: 14, marginBottom: 8, borderLeft: `3px solid ${mo?.color}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 22 }}>{mo?.emoji}</span>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{mo?.label}</span>
                      </div>
                      <span style={{ fontSize: 11, color: c.textMuted }}>{new Date(log.ts).toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                    {log.feelings.length > 0 && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: log.note ? 6 : 0 }}>
                        {log.feelings.map(f => <span key={f} style={{ fontSize: 11, padding: "2px 10px", borderRadius: 12, background: `${mo?.color}15`, color: mo?.color, border: `1px solid ${mo?.color}30` }}>{f}</span>)}
                      </div>
                    )}
                    {log.note && <p style={{ fontSize: 12, color: c.textDim, lineHeight: 1.5, fontStyle: "italic" }}>"{log.note}"</p>}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── VITALS TAB ── */}
          {tab === "vitals" && (
            <div style={{ animation: "fadeUp 0.3s ease" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 700 }}>Vitals Tracker</h2>
                {vitalFilter && <button onClick={() => setVitalFilter(null)} style={{ fontSize: 12, color: c.accent, background: "transparent", border: `1px solid ${c.border}`, borderRadius: 8, padding: "4px 12px", cursor: "pointer" }}>Show All</button>}
              </div>
              <p style={{ color: c.textDim, fontSize: 13, marginBottom: 16 }}>{vitalFilter ? `Showing detailed view for ${VITALS_CONFIG.find(v=>v.key===vitalFilter)?.label}` : "Tap any card on the dashboard to jump here"}</p>

              {/* Time Range Selector */}
              <div style={{ display: "flex", gap: 6, marginBottom: 20, background: c.surfaceLight, borderRadius: 12, padding: 4 }}>
                {[
                  { id: "week", label: "7 Days" },
                  { id: "month", label: "30 Days" },
                  { id: "3month", label: "3 Months" },
                ].map(r => (
                  <button key={r.id} onClick={() => setVitalTimeRange(r.id)} style={{
                    flex: 1, padding: "8px 0", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer",
                    background: vitalTimeRange === r.id ? "linear-gradient(135deg, #059669, #34d399)" : "transparent",
                    color: vitalTimeRange === r.id ? "#fff" : c.textMuted,
                    border: "none", transition: "all 0.2s",
                  }}>{r.label}</button>
                ))}
              </div>

              {VITALS_CONFIG
                .filter(v => !vitalFilter || v.key === vitalFilter)
                .map(v => {
                const data = vitalTimeRange === "week" ? MOCK_VITALS_WEEK : vitalTimeRange === "month" ? MOCK_VITALS_MONTH : MOCK_VITALS_3MONTH;
                const values = data.map(d => d[v.key]);
                const avg = (values.reduce((s,x) => s+x, 0) / values.length).toFixed(1);
                const min = Math.min(...values);
                const max = Math.max(...values);
                const isHighlighted = vitalFilter === v.key;
                const chartHeight = isHighlighted ? 180 : 100;

                return (
                  <div key={v.key} ref={el => vitalRefs.current[v.key] = el} style={{
                    ...cardStyle, marginBottom: 12,
                    borderColor: isHighlighted ? v.color : c.border,
                    boxShadow: isHighlighted ? `0 0 20px ${v.color}20` : "none",
                    transition: "all 0.3s",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 22 }}>{v.icon}</span>
                        <div>
                          <h4 style={{ fontSize: 15, fontWeight: 600 }}>{v.label}</h4>
                          <span style={{ fontSize: 11, color: c.textMuted }}>Normal: {v.range} · Ideal: {v.ideal}</span>
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span style={{ fontSize: 24, fontWeight: 700, color: v.color }}>{today[v.key]}</span>
                        <span style={{ fontSize: 12, color: c.textMuted, marginLeft: 3 }}>{v.unit}</span>
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                      {[
                        { label: "Avg", val: avg },
                        { label: "Min", val: min },
                        { label: "Max", val: max },
                      ].map(s => (
                        <div key={s.label} style={{ flex: 1, padding: "6px 10px", background: c.surfaceLight, borderRadius: 8, textAlign: "center" }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: v.color }}>{s.val}</div>
                          <div style={{ fontSize: 10, color: c.textMuted, textTransform: "uppercase" }}>{s.label}</div>
                        </div>
                      ))}
                    </div>

                    <ResponsiveContainer width="100%" height={chartHeight}>
                      <AreaChart data={data}>
                        <defs><linearGradient id={`grad-${v.key}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={v.color} stopOpacity={0.25}/>
                          <stop offset="100%" stopColor={v.color} stopOpacity={0}/>
                        </linearGradient></defs>
                        <XAxis dataKey="day" stroke={c.textMuted} fontSize={10} tickLine={false} axisLine={false} interval={vitalTimeRange === "week" ? 0 : "preserveStartEnd"}/>
                        <YAxis stroke={c.textMuted} fontSize={10} tickLine={false} axisLine={false} width={35}/>
                        <Tooltip contentStyle={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 10, fontSize: 12 }}/>
                        <Area type="monotone" dataKey={v.key} stroke={v.color} fill={`url(#grad-${v.key})`} strokeWidth={2} dot={vitalTimeRange === "week" ? { r: 3, fill: v.color } : false}/>
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── REPORTS TAB ── */}
          {tab === "reports" && (
            <div style={{ animation: "fadeUp 0.3s ease" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div><h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 700 }}>Reports</h2><p style={{ color: c.textDim, fontSize: 14 }}>Upload & analyze medical reports</p></div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => { setPasteMode(!pasteMode); }} style={{ ...btnPrimary, background: c.surfaceLight, color: c.textDim, border: `1px solid ${c.border}`, padding: "10px 16px" }}>📝 Paste</button>
                  <button onClick={() => fileRef.current?.click()} style={btnPrimary}>📄 Upload</button>
                </div>
                <input ref={fileRef} type="file" accept=".pdf,.txt,.csv" style={{ display: "none" }} onChange={handleReportUpload}/>
              </div>

              {/* Paste Mode */}
              {pasteMode && (
                <div style={{ ...cardStyle, marginBottom: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: c.accent, marginBottom: 8 }}>
                    {activeReport?.needsText ? "📋 PDF couldn't be read — paste the report text below" : "📋 Paste your report text directly"}
                  </h4>
                  <p style={{ fontSize: 12, color: c.textMuted, marginBottom: 12 }}>Copy-paste the text from your blood test / medical report below. Works great for screenshots or image-based PDFs.</p>
                  <textarea value={pasteText} onChange={e => setPasteText(e.target.value)}
                    placeholder={"Paste your report text here...\n\nExample:\nHemoglobin: 13.2 g/dL (Ref: 13.0-17.0)\nRBC Count: 4.8 million/μL (Ref: 4.5-5.5)\nWBC Count: 9500 /μL (Ref: 4000-11000)\n..."}
                    rows={8} style={{ ...inputStyle, resize: "vertical", fontSize: 13, lineHeight: 1.6 }} />
                  <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                    <button onClick={() => { setPasteMode(false); setPasteText(""); }} style={{ flex: 1, padding: 10, background: "transparent", border: `1px solid ${c.border}`, borderRadius: 10, color: c.textDim, cursor: "pointer", fontSize: 13 }}>Cancel</button>
                    <button onClick={activeReport?.needsText ? handlePasteSubmit : handleManualReport} disabled={!pasteText.trim()} style={{ ...btnPrimary, flex: 2, padding: 10, opacity: pasteText.trim() ? 1 : 0.4 }}>
                      ✓ Submit Report Text
                    </button>
                  </div>
                </div>
              )}

              {/* Report Archive */}
              {reports.length > 0 && reports.map(r => (
                <div key={r.id} onClick={() => { setActiveReport(r); setReportResult(savedResults[r.id] || null); }} style={{ ...cardStyle, padding: 14, marginBottom: 8, cursor: "pointer", borderColor: activeReport?.id===r.id?c.accent:c.border, background: activeReport?.id===r.id?c.accentDim:c.surface }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 24 }}>{r.needsText ? "⚠️" : "📋"}</span>
                      <div>
                        <p style={{ fontWeight: 600, fontSize: 14 }}>{r.name}</p>
                        <p style={{ fontSize: 11, color: c.textMuted }}>{r.date} · {r.size}{r.needsText ? " · Needs text input" : ""}</p>
                      </div>
                    </div>
                    <span style={{ fontSize: 12, color: c.accent }}>→</span>
                  </div>
                </div>
              ))}
              {reports.length === 0 && !pasteMode && <div style={{ ...cardStyle, padding: 60, textAlign: "center" }}><div style={{ fontSize: 48, marginBottom: 12 }}>📄</div><p style={{ fontWeight: 500, marginBottom: 6 }}>No reports yet</p><p style={{ color: c.textMuted, fontSize: 13 }}>Upload a PDF or paste report text to get started</p></div>}

              {/* Active Report Actions */}
              {activeReport && !activeReport.needsText && (
                <div style={{ marginTop: 16 }}>
                  {activeReport.text && <div style={{ ...cardStyle, padding: 14, marginBottom: 12, maxHeight: 120, overflowY: "auto" }}>
                    <p style={{ fontSize: 10, color: c.textMuted, marginBottom: 4, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>Extracted Text Preview</p>
                    <p style={{ fontSize: 11, color: c.textDim, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{activeReport.text.substring(0, 500)}{activeReport.text.length > 500 ? "..." : ""}</p>
                  </div>}
                  <button onClick={analyzeReport} disabled={loading==="report"} style={{ ...btnPrimary, width: "100%", marginBottom: 16, padding: 14 }}>{loading==="report"?"🔄 Analyzing...":"🔬 Analyze with AI"}</button>
                  {loading==="report" && <div style={{ textAlign: "center", padding: 40 }}><div style={{ width: 48, height: 48, margin: "0 auto 16px", borderRadius: "50%", border: `3px solid ${c.border}`, borderTopColor: c.accent, animation: "spin 1s linear infinite" }}/><p style={{ color: c.textDim, fontSize: 13 }}>Reading parameters and analyzing...</p></div>}
                  {reportResult && (
                    <div style={{ animation: "fadeUp 0.3s ease" }}>
                      <div style={{ ...cardStyle, background: "linear-gradient(135deg, #059669, #065f46)", marginBottom: 16, border: "none" }}><h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>📋 Summary</h3><p style={{ fontSize: 14, lineHeight: 1.7, opacity: 0.95 }}>{reportResult.summary}</p></div>
                      {["critical","borderline","normal"].map(status => {
                        const items = reportResult.parameters?.filter(p => p.status===status) || [];
                        if (!items.length) return null; const cfg = statusCfg[status];
                        return (<div key={status} style={{ marginBottom: 16 }}><h4 style={{ fontSize: 13, fontWeight: 600, color: cfg.color, marginBottom: 8 }}>{cfg.emoji} {status.charAt(0).toUpperCase()+status.slice(1)} ({items.length})</h4>
                          {items.map((p,i) => (<div key={i} style={{ ...cardStyle, borderLeft: `3px solid ${cfg.color}`, borderColor: cfg.border, background: cfg.bg, marginBottom: 8, padding: 16 }}><div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6, marginBottom: 6 }}><span style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</span><span style={{ fontWeight: 700, color: cfg.color }}>{p.value} <span style={{ fontSize: 11, color: c.textMuted }}>(ref: {p.range})</span></span></div><p style={{ fontSize: 12, color: c.textDim, lineHeight: 1.6 }}>{p.explanation}</p>{p.contextNote && <div style={{ marginTop: 8, padding: "8px 12px", background: "rgba(52,211,153,0.1)", borderRadius: 8, fontSize: 12, color: c.accent }}>💡 {p.contextNote}</div>}</div>))}
                        </div>);
                      })}
                      {reportResult.doctorFlags?.length > 0 && <div style={{ ...cardStyle, background: "rgba(248,113,113,0.06)", borderColor: "rgba(248,113,113,0.2)", marginBottom: 16 }}><h4 style={{ fontSize: 14, fontWeight: 700, color: c.danger, marginBottom: 12 }}>🏥 See a Doctor</h4>{reportResult.doctorFlags.map((f,i)=>(<div key={i} style={{ display:"flex",gap:10,alignItems:"flex-start",marginBottom:8 }}><span style={{ fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:6,background:f.urgency==="urgent"?c.danger:f.urgency==="soon"?c.warning:"#666",color:"#fff",textTransform:"uppercase" }}>{f.urgency}</span><p style={{ fontSize:13,color:c.textDim }}>{f.issue}</p></div>))}</div>}
                      {reportResult.lifestyle?.length > 0 && <div style={{ ...cardStyle, marginBottom: 16 }}><h4 style={{ fontSize: 14, fontWeight: 700, color: c.accent, marginBottom: 12 }}>🌿 Lifestyle</h4>{reportResult.lifestyle.map((t,i)=>(<p key={i} style={{ fontSize:13,color:c.textDim,marginBottom:8,paddingLeft:14,borderLeft:`2px solid ${c.accent}`,lineHeight:1.6 }}>{t}</p>))}</div>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── PROFILE TAB ── */}
          {tab === "profile" && (
            <div style={{ animation: "fadeUp 0.3s ease" }}>
              <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 700, marginBottom: 20 }}>Your Profile</h2>
              {[
                { section: "Basic Info", fields: [{ key:"name",label:"Name",ph:"Amrutanshu Dash"},{key:"age",label:"Age",ph:"20"},{key:"sex",label:"Sex",ph:"Male/Female"},{key:"weight",label:"Weight (kg)",ph:"70"},{key:"height",label:"Height (cm)",ph:"175"}]},
                { section: "Lifestyle", fields: [{ key:"occupation",label:"Occupation",ph:"Student / Professional"},{key:"occupationDetail",label:"Details",ph:"e.g., JEE aspirant"},{key:"sleepHrs",label:"Avg Sleep (hrs)",ph:"6"},{key:"freeTimeHrs",label:"Free Time for Fitness (hrs/day)",ph:"1.5"}]},
                { section: "Medical", fields: [{key:"medications",label:"Medications",ph:"e.g., Vitamin D"},{key:"familyHistory",label:"Family History",ph:"e.g., Father — diabetes"}]},
              ].map(s => (
                <div key={s.section} style={{ ...cardStyle, marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, color: c.accent, marginBottom: 14, textTransform: "uppercase", letterSpacing: 1 }}>{s.section}</h3>
                  {s.fields.map(f => (<div key={f.key} style={{ marginBottom: 14 }}><label style={{ fontSize: 12, fontWeight: 500, color: c.textMuted, marginBottom: 5, display: "block" }}>{f.label}</label><input value={profile[f.key]} onChange={e=>setProfile({...profile,[f.key]:e.target.value})} placeholder={f.ph} style={inputStyle} onFocus={e=>e.target.style.borderColor=c.accent} onBlur={e=>e.target.style.borderColor=c.border}/></div>))}
                </div>
              ))}
              <div style={{ ...cardStyle, marginBottom: 16 }}><h3 style={{ fontSize: 14, fontWeight: 600, color: c.accent, marginBottom: 14, textTransform: "uppercase", letterSpacing: 1 }}>Food Preference</h3><div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{FOOD_PREFS.map(f => <span key={f} onClick={()=>setProfile({...profile,foodPref:f})} style={chipStyle(profile.foodPref===f)}>{f}</span>)}</div></div>
              <div style={{ ...cardStyle, marginBottom: 16 }}><h3 style={{ fontSize: 14, fontWeight: 600, color: c.accent, marginBottom: 14, textTransform: "uppercase", letterSpacing: 1 }}>Sports</h3><div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{SPORTS.map(s => <span key={s} onClick={()=>setProfile({...profile,sports:profile.sports.includes(s)?profile.sports.filter(x=>x!==s):[...profile.sports,s]})} style={chipStyle(profile.sports.includes(s))}>{s}</span>)}</div></div>
              <div style={{ ...cardStyle, marginBottom: 16 }}><h3 style={{ fontSize: 14, fontWeight: 600, color: c.accent, marginBottom: 14, textTransform: "uppercase", letterSpacing: 1 }}>Health Conditions</h3><div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{CONDITIONS.map(c2 => <span key={c2} onClick={()=>setProfile({...profile,conditions:profile.conditions.includes(c2)?profile.conditions.filter(x=>x!==c2):[...profile.conditions,c2]})} style={chipStyle(profile.conditions.includes(c2))}>{c2}</span>)}</div></div>
              <button onClick={()=>{setProfileSaved(true);setTab("dashboard")}} style={{ ...btnPrimary, width: "100%", padding: 14, fontSize: 15 }}>💾 Save Profile</button>
              <p style={{ textAlign: "center", fontSize: 11, color: c.textMuted, marginTop: 10 }}>🔒 All data stored locally. Never sent to any server.</p>
            </div>
          )}

          {/* ── AI INSIGHTS TAB ── */}
          {tab === "insights" && (
            <div style={{ animation: "fadeUp 0.3s ease" }}>
              <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 700, marginBottom: 20 }}>AI Insights</h2>
              {!aiPlan && loading !== "plan" && (
                <div style={{ ...cardStyle, textAlign: "center", padding: 40 }}><div style={{ fontSize: 48, marginBottom: 16 }}>🤖</div><p style={{ fontWeight: 500, marginBottom: 6 }}>Generate your personalized plan</p><p style={{ color: c.textMuted, fontSize: 13, marginBottom: 20 }}>{profileSaved?"Based on your profile & vitals":"Save profile first"}</p><button onClick={generatePlan} style={btnPrimary}>✨ Generate Plan</button></div>
              )}
              {loading === "plan" && <div style={{ textAlign: "center", padding: 50 }}><div style={{ width: 56, height: 56, margin: "0 auto 20px", borderRadius: "50%", border: `3px solid ${c.border}`, borderTopColor: c.accent, animation: "spin 1s linear infinite" }}/><p style={{ fontWeight: 600, fontSize: 16 }}>Crafting your plan...</p></div>}
              {aiPlan && (
                <div style={{ animation: "fadeUp 0.3s ease" }}>
                  {aiPlan.keyInsights && <div style={{ ...cardStyle, background: "linear-gradient(135deg,#1e1b4b,#312e81)", border: "none", marginBottom: 16 }}><h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12, color: "#a5b4fc" }}>💡 Key Insights</h3>{aiPlan.keyInsights.map((k,i)=><p key={i} style={{ fontSize:13,color:"#c7d2fe",lineHeight:1.7,marginBottom:8,paddingLeft:14,borderLeft:"2px solid #6366f1" }}>{k}</p>)}</div>}
                  {aiPlan.dietPlan && <div style={{ ...cardStyle, marginBottom: 16 }}><h3 style={{ fontSize: 15, fontWeight: 700, color: c.accent, marginBottom: 6 }}>🥗 Diet Plan</h3><p style={{ fontSize: 12, color: c.textMuted, marginBottom: 14 }}>{aiPlan.dietPlan.philosophy}</p>{aiPlan.dietPlan.meals?.map((m,i)=><div key={i} style={{ padding:14,background:c.surfaceLight,borderRadius:12,marginBottom:8,border:`1px solid ${c.border}` }}><div style={{ display:"flex",justifyContent:"space-between",marginBottom:6 }}><span style={{ fontWeight:600,fontSize:14 }}>{m.meal}</span><div style={{ display:"flex",gap:10 }}><span style={{ fontSize:11,color:c.textMuted }}>{m.time}</span><span style={{ fontSize:11,color:c.accent,fontWeight:600 }}>{m.calories} kcal</span></div></div><p style={{ fontSize:13,color:c.text,lineHeight:1.5 }}>{m.suggestion}</p>{m.notes&&<p style={{ fontSize:11,color:c.textMuted,marginTop:4,fontStyle:"italic" }}>{m.notes}</p>}</div>)}</div>}
                  {aiPlan.trainingPlan && <div style={{ ...cardStyle, marginBottom: 16 }}><h3 style={{ fontSize: 15, fontWeight: 700, color: "#fb923c", marginBottom: 6 }}>🏋️ Training</h3><p style={{ fontSize: 12, color: c.textMuted, marginBottom: 14 }}>{aiPlan.trainingPlan.philosophy}</p>{aiPlan.trainingPlan.weeklySchedule?.map((d,i)=><div key={i} style={{ display:"flex",gap:12,alignItems:"center",padding:"10px 14px",background:c.surfaceLight,borderRadius:10,marginBottom:6,border:`1px solid ${c.border}` }}><span style={{ fontWeight:700,fontSize:13,color:"#fb923c",width:32 }}>{d.day}</span><div style={{ flex:1 }}><span style={{ fontWeight:600,fontSize:13 }}>{d.activity}</span><span style={{ fontSize:11,color:c.textMuted,marginLeft:8 }}>{d.duration}</span></div><span style={{ fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:6,textTransform:"uppercase",background:d.intensity==="high"?"rgba(248,113,113,0.15)":d.intensity==="moderate"?"rgba(251,191,36,0.15)":"rgba(52,211,153,0.15)",color:d.intensity==="high"?c.danger:d.intensity==="moderate"?c.warning:c.accent }}>{d.intensity}</span></div>)}</div>}
                  {aiPlan.sleepAdvice && <div style={{ ...cardStyle, marginBottom: 16 }}><h3 style={{ fontSize: 15, fontWeight: 700, color: "#818cf8", marginBottom: 8 }}>😴 Sleep</h3><p style={{ fontSize: 13, color: c.textDim, lineHeight: 1.7 }}>{aiPlan.sleepAdvice}</p></div>}
                  <button onClick={generatePlan} style={{ ...btnPrimary, width: "100%", background: c.surfaceLight, color: c.textDim, border: `1px solid ${c.border}` }}>🔄 Regenerate</button>
                </div>
              )}
            </div>
          )}
        </main>

        {/* ═══ BOTTOM NAV ═══ */}
        <nav style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(10,15,26,0.95)", backdropFilter: "blur(20px)", borderTop: `1px solid ${c.border}`, padding: "6px 0 10px", display: "flex", justifyContent: "center", zIndex: 100 }}>
          {[
            { id: "dashboard", icon: "🏠", label: "Home" },
            { id: "workout", icon: "💪", label: "Workout" },
            { id: "mood", icon: "🧠", label: "Mood" },
            { id: "vitals", icon: "📊", label: "Vitals" },
            { id: "reports", icon: "📋", label: "Reports" },
            { id: "profile", icon: "🧑", label: "Profile" },
            { id: "insights", icon: "🤖", label: "AI Plan" },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 1, background: "transparent", border: "none", cursor: "pointer", color: tab === t.id ? c.accent : c.textMuted, padding: "4px 0" }}>
              <span style={{ fontSize: 18, filter: tab === t.id ? "drop-shadow(0 0 6px rgba(52,211,153,0.5))" : "none" }}>{t.icon}</span>
              <span style={{ fontSize: 9, fontWeight: tab === t.id ? 600 : 400 }}>{t.label}</span>
              {tab === t.id && <div style={{ width: 14, height: 2, borderRadius: 1, background: c.accent, marginTop: 1 }}/>}
            </button>
          ))}
        </nav>

        <div style={{ textAlign: "center", padding: "16px 20px 100px", fontSize: 11, color: c.textMuted }}>
          ⚕️ Sehat Saathi is not medical advice. Always consult a healthcare professional.
        </div>
      </div>
    </>
  );
}
